import React, { useMemo, useState } from 'react';
import { Box, Button, CircularProgress, TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import {
  Download as DownloadIcon,
  KeyboardArrowDown as ExpandedIcon,
  KeyboardArrowRight as CollapsedIcon,
} from '@mui/icons-material';
import { formatHours } from './format';
import { cellBase, stickyLeft, stickyRight } from './tableStyles';
import MonthGrid, { measureDayColumns, measureRightColumn } from './MonthGrid';

const DAILY_MINUTES = 480;
const NO_TEAM = 'No team';
const TEAM_BG = 'var(--rt-row-team)';
const RIGHT_COLUMNS = [{ key: 'total', label: 'Tot (h)' }];

/** Sfondo cella progetto: verde più intenso quante più ore rispetto al massimo del mese. */
function projectShade(minutes, maxMinutes) {
  if (!minutes || !maxMinutes) return undefined;
  const alpha = 0.06 + 0.3 * Math.min(1, minutes / maxMinutes);
  return `rgba(90,176,49,${alpha.toFixed(2)})`;
}

function personCell(minutes, absence) {
  if (absence?.full_day) return { bg: 'rgba(229,57,53,0.12)', title: 'Absence · full day' };
  if (absence) return { bg: 'rgba(249,168,37,0.18)', title: 'Absence · partial' };
  if (minutes > DAILY_MINUTES) return { bg: 'rgba(239,108,0,0.16)', title: 'Over 8h' };
  return { bg: undefined, title: '' };
}

function PersonRow({ person, monthDays, absences, indent }) {
  return (
    <TableRow sx={{ bgcolor: 'var(--rt-row-sub)' }}>
      <TableCell sx={{ ...cellBase, ...stickyLeft, bgcolor: 'var(--rt-row-sub)', textAlign: 'left', pl: indent, pr: 1 }}>
        <Typography variant="body2" noWrap sx={{ fontSize: 13, color: person.total ? 'text.primary' : 'text.disabled' }}>
          {person.name}
        </Typography>
      </TableCell>
      {monthDays.map((d) => {
        const minutes = person.days[d.day] || 0;
        const absence = absences?.get(d.day);
        const style = personCell(minutes, absence);
        return (
          <TableCell
            key={d.day}
            title={style.title}
            sx={{ ...cellBase, bgcolor: style.bg || (d.weekend ? 'var(--rt-grey-50)' : 'var(--rt-row-sub)'), color: 'text.primary' }}
          >
            {minutes ? formatHours(minutes) : absence?.full_day ? <span style={{ color: 'var(--rt-danger)', fontSize: 10 }}>OFF</span> : ''}
          </TableCell>
        );
      })}
      <TableCell sx={{ ...cellBase, ...stickyRight, bgcolor: 'var(--rt-row-sub)', fontWeight: 600, color: person.total ? 'text.primary' : 'text.disabled' }}>
        {formatHours(person.total)}
      </TableCell>
    </TableRow>
  );
}

/** Raggruppa le persone per team (ordine alfabetico, "No team" in fondo) con totali per giorno. */
function groupByTeam(people) {
  const map = new Map();
  people.forEach((p) => {
    const name = p.team || NO_TEAM;
    if (!map.has(name)) map.set(name, { name, people: [], days: {}, total: 0 });
    const t = map.get(name);
    t.people.push(p);
    t.total += p.total;
    Object.entries(p.days).forEach(([day, minutes]) => {
      t.days[day] = (t.days[day] || 0) + minutes;
    });
  });
  return [...map.values()].sort((a, b) =>
    a.name === NO_TEAM ? 1 : b.name === NO_TEAM ? -1 : a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
  );
}

function PersonRows({ project, detail, monthDays, absencesByUserId, onExportAbsences, exportingAbsences }) {
  const colSpan = monthDays.length + 2;
  const [openTeams, setOpenTeams] = useState(() => new Set());
  const teams = detail?.people ? groupByTeam(detail.people) : [];
  const toggleTeam = (name) =>
    setOpenTeams((set) => {
      const next = new Set(set);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  if (!detail || detail.loading) {
    return (
      <TableRow>
        <TableCell colSpan={colSpan} sx={{ ...cellBase, textAlign: 'left', bgcolor: 'var(--rt-row-sub)' }}>
          <Box sx={{ position: 'sticky', left: 0, display: 'inline-flex', alignItems: 'center', gap: 1, px: 6 }}>
            <CircularProgress size={14} />
            <Typography variant="body2" color="text.secondary">
              Loading people…
            </Typography>
          </Box>
        </TableCell>
      </TableRow>
    );
  }
  if (detail.error) {
    return (
      <TableRow>
        <TableCell colSpan={colSpan} sx={{ ...cellBase, textAlign: 'left', bgcolor: 'var(--rt-row-sub)' }}>
          <Typography variant="body2" color="error" sx={{ position: 'sticky', left: 0, px: 6, display: 'inline-block' }}>
            {detail.error}
          </Typography>
        </TableCell>
      </TableRow>
    );
  }

  const showTeams = teams.length > 1 || (teams.length === 1 && teams[0].name !== NO_TEAM);

  return (
    <>
      {showTeams
        ? teams.map((t) => {
            const isOpen = openTeams.has(t.name);
            return (
              <React.Fragment key={t.name}>
                <TableRow onClick={() => toggleTeam(t.name)} sx={{ cursor: 'pointer', '&:hover td': { filter: 'brightness(0.97)' } }}>
                  <TableCell sx={{ ...cellBase, ...stickyLeft, bgcolor: TEAM_BG, textAlign: 'left', pl: 3, pr: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                      {isOpen ? (
                        <ExpandedIcon fontSize="small" sx={{ color: 'success.main', flexShrink: 0 }} />
                      ) : (
                        <CollapsedIcon fontSize="small" sx={{ color: 'text.secondary', flexShrink: 0 }} />
                      )}
                      <Typography variant="body2" noWrap sx={{ fontSize: 13, fontWeight: 600 }}>
                        {t.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                        · {t.people.length}
                      </Typography>
                    </Box>
                  </TableCell>
                  {monthDays.map((d) => (
                    <TableCell key={d.day} sx={{ ...cellBase, fontWeight: 600, bgcolor: d.weekend ? 'var(--rt-row-team-weekend)' : TEAM_BG }}>
                      {t.days[d.day] ? formatHours(t.days[d.day]) : ''}
                    </TableCell>
                  ))}
                  <TableCell sx={{ ...cellBase, ...stickyRight, bgcolor: TEAM_BG, fontWeight: 700 }}>{formatHours(t.total)}</TableCell>
                </TableRow>
                {isOpen &&
                  t.people.map((p) => (
                    <PersonRow key={p.key} person={p} monthDays={monthDays} absences={p.userId ? absencesByUserId[p.userId] : null} indent={7} />
                  ))}
              </React.Fragment>
            );
          })
        : detail.people.map((p) => (
            <PersonRow key={p.key} person={p} monthDays={monthDays} absences={p.userId ? absencesByUserId[p.userId] : null} indent={6} />
          ))}
      <TableRow sx={{ bgcolor: 'var(--rt-row-sub)' }}>
        <TableCell colSpan={colSpan} sx={{ ...cellBase, textAlign: 'left', py: 0.75, borderBottom: '1px solid var(--rt-grid-border)' }}>
          <Box sx={{ position: 'sticky', left: 0, display: 'inline-flex', gap: 1, pl: 6 }}>
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              startIcon={exportingAbsences ? <CircularProgress size={14} color="inherit" /> : <DownloadIcon fontSize="small" />}
              disabled={exportingAbsences || !detail.projectUsers?.length}
              onClick={() => onExportAbsences(project, detail)}
              sx={{ textTransform: 'none', borderColor: 'divider', bgcolor: 'background.paper' }}
            >
              Export absences
            </Button>
          </Box>
        </TableCell>
      </TableRow>
    </>
  );
}

/** Tabella mese: una riga per progetto, espandibile sulle persone del progetto. */
export default function MonthlyPivotTable({
  monthDays,
  rows,
  loading,
  expanded,
  onToggle,
  details,
  absencesByUserId,
  onExportAbsences,
  exportingAbsencesFor,
}) {
  const maxMinutes = Math.max(0, ...rows.flatMap((r) => Object.values(r.days)));
  const dayTotals = monthDays.map((d) => rows.reduce((sum, r) => sum + (r.days[d.day] || 0), 0));
  const grandTotal = rows.reduce((sum, r) => sum + r.total, 0);

  // Larghezza di ogni colonna giorno = la minima prevista, allargata se un valore (anche nei dettagli aperti) non ci sta
  const { dayWidths, totalWidth } = useMemo(() => {
    const totalTexts = [formatHours(grandTotal)];
    const widths = measureDayColumns(monthDays, (consider) => {
      const considerRow = (days, total) => {
        Object.entries(days).forEach(([day, minutes]) => consider(Number(day), minutes ? formatHours(minutes) : ''));
        totalTexts.push(formatHours(total));
      };
      rows.forEach((r) => considerRow(r.days, r.total));
      dayTotals.forEach((m, i) => consider(i + 1, m ? formatHours(m) : ''));
      expanded.forEach((pid) => {
        const people = details[pid]?.people || [];
        people.forEach((p) => considerRow(p.days, p.total));
        groupByTeam(people).forEach((t) => considerRow(t.days, t.total));
      });
    });
    return { dayWidths: widths, totalWidth: measureRightColumn(...totalTexts) };
  }, [rows, dayTotals, grandTotal, expanded, details, monthDays]);

  return (
    <MonthGrid
      monthDays={monthDays}
      firstColLabel="Project"
      dayWidths={dayWidths}
      rightColumns={RIGHT_COLUMNS}
      rightWidth={totalWidth}
      loading={loading}
      fillDeps={[rows.length]}
      totalRow={
        rows.length
          ? {
              cell: (d, i) => ({ content: dayTotals[i] ? formatHours(dayTotals[i]) : '' }),
              right: [{ content: formatHours(grandTotal) }],
            }
          : null
      }
    >
      {rows.map((r) => {
        const isOpen = expanded.has(r.id);
        return (
          <React.Fragment key={r.id}>
            <TableRow
              hover
              onClick={() => onToggle(r.id)}
              sx={{ cursor: 'pointer', '&:hover td': { filter: 'brightness(0.97)' } }}
            >
              <TableCell sx={{ ...cellBase, ...stickyLeft, textAlign: 'left', pl: 1, pr: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                  {isOpen ? (
                    <ExpandedIcon fontSize="small" sx={{ color: 'success.main', flexShrink: 0 }} />
                  ) : (
                    <CollapsedIcon fontSize="small" sx={{ color: 'text.secondary', flexShrink: 0 }} />
                  )}
                  <Box sx={{ minWidth: 0 }}>
                    <Tooltip title={r.name} placement="top-start" enterDelay={600}>
                      <Typography variant="body2" noWrap sx={{ fontWeight: 600, fontSize: 13 }}>
                        {r.name}
                      </Typography>
                    </Tooltip>
                    {r.customer && (
                      <Typography variant="caption" color="text.secondary" noWrap component="div" sx={{ lineHeight: 1.1, mt: -0.25 }}>
                        {r.customer}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </TableCell>
              {monthDays.map((d) => {
                const minutes = r.days[d.day] || 0;
                return (
                  <TableCell
                    key={d.day}
                    sx={{ ...cellBase, bgcolor: projectShade(minutes, maxMinutes) || (d.weekend ? 'var(--rt-grey-50)' : 'background.paper') }}
                  >
                    {minutes ? formatHours(minutes) : ''}
                  </TableCell>
                );
              })}
              <TableCell sx={{ ...cellBase, ...stickyRight, fontWeight: 700 }}>{formatHours(r.total)}</TableCell>
            </TableRow>
            {isOpen && (
              <PersonRows
                project={r}
                detail={details[r.id]}
                monthDays={monthDays}
                absencesByUserId={absencesByUserId}
                onExportAbsences={onExportAbsences}
                exportingAbsences={exportingAbsencesFor === r.id}
              />
            )}
          </React.Fragment>
        );
      })}
    </MonthGrid>
  );
}
